import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./CompanyDetails.css";
import Footer from "./footer";
import AdminPositions from "./AdminPositions";
import Blogs from "./Blogs";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import TurndownService from "turndown";



const API_BASE = "https://api.niveshly.com";

const CompanyDetails = () => {
  const navigate = useNavigate();
  const [companyDetails, setCompanyDetails] = useState([]);
  const [newDetail, setNewDetail] = useState({
    company_id: "",
    reports: [], // 🆕 Array of {report_type, report_year, report_url}
    key_people: [],
    about_company: "",
    exchange_type: "",
    highlights: [],
  });
  const turndownService = new TurndownService({
    headingStyle: "atx",
    bulletListMarker: "-",
    codeBlockStyle: "fenced",
  });
  const handleAboutPaste = (e) => {
    e.preventDefault();

    const clipboardData = e.clipboardData;
    const htmlData = clipboardData.getData("text/html");
    const textData = clipboardData.getData("text/plain");

    let finalHTML = "";

    if (htmlData) {
      // Convert pasted rich HTML to clean HTML
      finalHTML = htmlData;
    } else {
      // Plain text → wrap into paragraphs
      finalHTML = textData
        .split("\n")
        .map((line) => `<p>${line}</p>`)
        .join("");
    }

    setNewDetail((prev) => ({
      ...prev,
      about_company: prev.about_company + finalHTML,
    }));
  };

  const [editIndex, setEditIndex] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [keyPersonForm, setKeyPersonForm] = useState({ title: "", name: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [fundamentals, setFundamentals] = useState([]);
  const [fundamentalField, setFundamentalField] = useState({ key: "", value: "" });
  const [highlightText, setHighlightText] = useState("");
  const [growth, setGrowth] = useState([]);
  const [growthField, setGrowthField] = useState({ key: "", value: "" });

  // 🆕 New state for report form with year
  const [reportForm, setReportForm] = useState({
    report_type: "Financial",
    report_year: new Date().getFullYear(),
    report_url: ""
  });

  // Fetch companies for dropdown
  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API_BASE}/companies`);
      if (!res.ok) throw new Error("Failed to fetch companies");
      const data = await res.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch companies:", err);
      setError("Failed to load companies");
      setCompanies([]);
    }
  };

  // Fetch all company details
  const fetchCompanyDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/company-details`);
      if (!res.ok) throw new Error("Failed to fetch company details");
      const data = await res.json();
      setCompanyDetails(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch company details:", err);
      setError("Failed to load company details");
      setCompanyDetails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    fetchCompanyDetails();
  }, []);

  // 🆕 Add report with year
  const handleAddReport = () => {
    if (!reportForm.report_type || !reportForm.report_year || !reportForm.report_url) {
      alert("Please fill report type, year, and URL");
      return;
    }
    setNewDetail({
      ...newDetail,
      reports: [...newDetail.reports, { ...reportForm }],
    });
    setReportForm({
      report_type: "Financial",
      report_year: new Date().getFullYear(),
      report_url: ""
    });
  };

  // 🆕 Remove report
  const handleRemoveReport = (index) => {
    const updated = newDetail.reports.filter((_, i) => i !== index);
    setNewDetail({ ...newDetail, reports: updated });
  };

  // Add key person
  const handleAddKeyPerson = () => {
    if (!keyPersonForm.title || !keyPersonForm.name) {
      alert("Please fill both title and name");
      return;
    }
    setNewDetail({
      ...newDetail,
      key_people: [...newDetail.key_people, { ...keyPersonForm }],
    });
    setKeyPersonForm({ title: "", name: "" });
  };

  // Remove key person
  const handleRemoveKeyPerson = (index) => {
    const updated = newDetail.key_people.filter((_, i) => i !== index);
    setNewDetail({ ...newDetail, key_people: updated });
  };

  // Add highlight
  const handleAddHighlight = () => {
    if (!highlightText.trim()) {
      alert("Please enter a highlight");
      return;
    }
    setNewDetail({
      ...newDetail,
      highlights: [...newDetail.highlights, highlightText],
    });
    setHighlightText("");
  };

  // Remove highlight
  const handleRemoveHighlight = (index) => {
    const updated = newDetail.highlights.filter((_, i) => i !== index);
    setNewDetail({ ...newDetail, highlights: updated });
  };

  // Handle paste to preserve formatting
  const handleHighlightPaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');

    const content = html || text;
    setHighlightText(content);
  };
  // 🆕 Add growth dictionary
  const growthDict = Object.fromEntries(
    growth.map(item => [item.key, item.value])
  );

  const handleSaveDetails = async () => {
    const { company_id, reports } = newDetail;
    if (!company_id) {
      alert("Please select a company!");
      return;
    }

    // if (reports.length === 0) {
    //   alert("Please add at least one report!");
    //   return;
    // }

    const fundamentalsDict = Object.fromEntries(
      fundamentals.map(item => [item.key, item.value])
    );

    const payload = { ...newDetail, fundamentals: fundamentalsDict };

    try {
      if (editIndex !== null) {
        const id = companyDetails[editIndex].id;
        const response = await fetch(`${API_BASE}/company-details/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error("Failed to update details");
        alert("Details updated!");
        setEditIndex(null);
      } else {
        const response = await fetch(`${API_BASE}/company-details`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error("Failed to add details");
        alert("Details added!");
      }

      setNewDetail({
        company_id: "",
        reports: [],
        key_people: [],
        about_company: "",
        exchange_type: "",
        highlights: [],
      });
      setFundamentals([]);
      fetchCompanyDetails();
    } catch (err) {
      alert("Error saving details: " + err.message);
      console.error(err);
    }
  };

  // Delete company details
  const handleDeleteDetails = async (id) => {
    if (!window.confirm("Are you sure you want to delete these details?")) return;
    try {
      const response = await fetch(`${API_BASE}/company-details/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete details");
      fetchCompanyDetails();
    } catch (err) {
      alert("Failed to delete details: " + err.message);
    }
  };

  // Edit company details
  const handleEditDetails = (index) => {
    setEditIndex(index);
    const detail = companyDetails[index];
    setNewDetail({
      company_id: detail.company_id,
      reports: detail.reports || [],
      key_people: detail.key_people || [],
      about_company: detail.about_company || "",
      exchange_type: detail.exchange_type || "",
      highlights: detail.highlights || [],
    });
    setFundamentals(
      detail.fundamentals
        ? Object.entries(detail.fundamentals).map(([k, v]) => ({ key: k, value: v }))
        : []
    );
    // 🆕 Set growth data
    setGrowth(
      detail.growth
        ? Object.entries(detail.growth).map(([k, v]) => ({ key: k, value: v }))
        : []
    );
  };

  // Excel upload
  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!validTypes.includes(file.type)) {
      alert("Please upload a valid Excel file (.xlsx or .xls)");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("sheet_name", "CompanyDetails");

    try {
      const res = await fetch(`${API_BASE}/company-details/bulk-upload`, {
        method: "POST",
        body: formData,
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.detail || "Upload failed");
      }

      alert(
        `Upload Successful!\nInserted: ${result.inserted}\nSkipped: ${result.skipped}` +
        (result.errors && result.errors.length > 0 ? `\n\nErrors:\n${result.errors.join("\n")}` : "")
      );

      fetchCompanyDetails();
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("Upload error:", err);
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };
  const payload = {
    ...newDetail,
    growth: growthDict  // 🆕 Include growth
  };

  const getCompanyName = (companyId) => {
    const company = companies.find((c) => c.id === companyId);
    return company ? company.name : "Unknown Company";
  };
  // Add this state variable with your other useState declarations
  const [editingHighlightIndex, setEditingHighlightIndex] = useState(null);

  // Replace your handleAddHighlight function with this
  // const handleAddHighlight = () => {
  //   if (!highlightText.trim()) return;

  //   const formatted = formatText(highlightText);

  //   if (editingHighlightIndex !== null) {
  //     // Update existing highlight
  //     const updated = [...newDetail.highlights];
  //     updated[editingHighlightIndex] = formatted;
  //     setNewDetail({ ...newDetail, highlights: updated });
  //     setEditingHighlightIndex(null);
  //   } else {
  //     // Add new highlight
  //     setNewDetail({
  //       ...newDetail,
  //       highlights: [...newDetail.highlights, formatted],
  //     });
  //   }
  //   setHighlightText("");
  // };

  // Add these new handler functions
  const handleEditHighlight = (index) => {
    const highlight = newDetail.highlights[index];
    // Convert HTML back to markdown-style text for editing
    const plainText = highlight
      .replace(/<strong>(.*?)<\/strong>/g, "**$1**")
      .replace(/<br\s*\/?>/g, "\n");
    setHighlightText(plainText);
    setEditingHighlightIndex(index);
  };

  const handleMoveHighlight = (index, direction) => {
    const newHighlights = [...newDetail.highlights];
    if (direction === "up" && index > 0) {
      [newHighlights[index], newHighlights[index - 1]] =
        [newHighlights[index - 1], newHighlights[index]];
    } else if (direction === "down" && index < newHighlights.length - 1) {
      [newHighlights[index], newHighlights[index + 1]] =
        [newHighlights[index + 1], newHighlights[index]];
    }
    setNewDetail({ ...newDetail, highlights: newHighlights });
  };

  // // Update your handleRemoveHighlight to clear editing state
  // const handleRemoveHighlight = (index) => {
  //   setNewDetail({
  //     ...newDetail,
  //     highlights: newDetail.highlights.filter((_, i) => i !== index),
  //   });
  //   // If we're editing this highlight, clear the edit state
  //   if (editingHighlightIndex === index) {
  //     setEditingHighlightIndex(null);
  //     setHighlightText("");
  //   }
  // };

  // Replace your Highlights Section JSX with this:

  return (
    <div className="details-container">
      <header className="details-header">
        <img
          src="WhatsApp_Image_2025-02-10_at_10.54.09_cf865204-removebg-preview.webp"
          alt="Logo"
          className="logo"
        />
        <h1>Manage Company Details</h1>
        <button className="back-btn" onClick={() => navigate("/manage-companies")}>
          ← Back to Companies
        </button>
      </header>

      {/* Excel Upload Section */}
      <div className="upload-section">
        <h2>Bulk Upload via Excel</h2>
        <div className="upload-controls">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleExcelUpload}
            disabled={uploading}
          />
          <button className="upload-btn" disabled={uploading}>
            {uploading ? "Uploading..." : "Upload Excel"}
          </button>
        </div>
        <p className="upload-hint">
          <strong>Sheet Name:</strong> CompanyDetails
          <br />
          <strong>Required columns:</strong> company_id
          <br />
          <strong>Reports Format:</strong> type1|year1|url1;type2|year2|url2
          <br />
          <em>Example: Financial|2024|https://example.com/report1.pdf;Annual|2023|https://example.com/report2.pdf</em>
        </p>
      </div>
      <AdminPositions />

      {/* Form Section */}
      <div className="form-section">
        <h2>{editIndex !== null ? "Edit Company Details" : "Add Company Details"}</h2>

        <label>Select Company *</label>
        <select
          value={newDetail.company_id}
          onChange={(e) => setNewDetail({ ...newDetail, company_id: e.target.value })}
        >
          <option value="">-- Select Company --</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </select>

        {/* 🆕 Reports Section with Year */}
        <div className="reports-section">
          <h3>Company Reports * (Add Multiple Reports & Years)</h3>
          <div className="report-form">
            <select
              value={reportForm.report_type}
              onChange={(e) => setReportForm({ ...reportForm, report_type: e.target.value })}
            >
              <option value="Financial">Financial Report</option>
              <option value="Annual">Annual Report</option>
              <option value="Quarterly">Quarterly Report</option>
              <option value="Investor Presentation">Investor Presentation</option>
              <option value="Prospectus">Prospectus</option>
              <option value="RHP">RHP (Red Herring Prospectus)</option>
              <option value="DRHP">DRHP (Draft Red Herring Prospectus)</option>
              <option value="Compliance">Compliance Report</option>
              <option value="ESG">ESG Report</option>
              <option value="Sustainability">Sustainability Report</option>
              <option value="Other">Other</option>
            </select>
            <input
              type="number"
              placeholder="Year"
              value={reportForm.report_year}
              onChange={(e) => setReportForm({ ...reportForm, report_year: e.target.value })}
              min="1900"
              max="2100"
            />
            <input
              type="url"
              placeholder="Report URL"
              value={reportForm.report_url}
              onChange={(e) => setReportForm({ ...reportForm, report_url: e.target.value })}
            />
            <button type="button" className="add-person-btn" onClick={handleAddReport}>
              + Add Report
            </button>
          </div>

          {newDetail.reports.length > 0 && (
            <div className="reports-list">
              {newDetail.reports.map((report, index) => (
                <div key={index} className="report-item">
                  <div className="report-info">
                    <span className="report-type-badge">{report.report_type}</span>
                    <span className="report-year-badge">{report.report_year}</span>
                    <a href={report.report_url} target="_blank" rel="noopener noreferrer" className="report-link">
                      View Report →
                    </a>
                  </div>
                  <button onClick={() => handleRemoveReport(index)}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Exchange Type Field
        <label>Exchange Type (e.g., NSE Growth, BSE SME)</label>
        <input
          type="text"
          placeholder="Enter exchange type"
          value={newDetail.exchange_type}
          onChange={(e) => setNewDetail({ ...newDetail, exchange_type: e.target.value })}
        /> */}

        {/* About Company Field */}
        {/* About Company Field */}
        <label>About Company (HTML Supported)</label>
        <textarea
          placeholder="Paste anything here — it will auto convert to HTML"
          rows={6}
          value={newDetail.about_company}
          onChange={(e) =>
            setNewDetail({ ...newDetail, about_company: e.target.value })
          }
          onPaste={handleAboutPaste}
        />
        <div
          style={{
            background: "#111",
            color: "white",
            padding: "10px",
            marginTop: "10px",
            borderRadius: "6px",
          }}
          dangerouslySetInnerHTML={{ __html: newDetail.about_company }}
        />


        {/* Fundamentals Section */}
        <div className="fundamentals-section">
          <h3>Fundamentals</h3>
          <div className="fundamental-form">
            <input
              type="text"
              placeholder="Field name (ex: P/E Ratio)"
              value={fundamentalField.key}
              onChange={(e) => setFundamentalField({ ...fundamentalField, key: e.target.value })}
            />
            <input
              type="text"
              placeholder="Value (ex: 91.17)"
              value={fundamentalField.value}
              onChange={(e) => setFundamentalField({ ...fundamentalField, value: e.target.value })}
            />
            <button
              type="button"
              className="add-person-btn"
              onClick={() => {
                if (!fundamentalField.key || !fundamentalField.value) return;
                setFundamentals([...fundamentals, { ...fundamentalField }]);
                setFundamentalField({ key: "", value: "" });
              }}
            >
              + Add Field
            </button>
          </div>

          {fundamentals.length > 0 && (
            <div className="fundamentals-list">
              {fundamentals.map((item, idx) => (
                <div key={idx} className="fundamental-item">
                  <span><strong>{item.key}:</strong> {item.value}</span>
                  <button onClick={() => setFundamentals(fundamentals.filter((_, i) => i !== idx))}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Growth Section */}
        {/* <div className="fundamentals-section">
  <h3>Growth Metrics</h3>
  <div className="fundamental-form">
    <input
      type="text"
      placeholder="Metric name (ex: Revenue Growth)"
      value={growthField.key}
      onChange={(e) => setGrowthField({ ...growthField, key: e.target.value })}
    />
    <input
      type="text"
      placeholder="Value (ex: 25%)"
      value={growthField.value}
      onChange={(e) => setGrowthField({ ...growthField, value: e.target.value })}
    />
    <button
      type="button"
      className="add-person-btn"
      onClick={() => {
        if (!growthField.key || !growthField.value) return;
        setGrowth([...growth, { ...growthField }]);
        setGrowthField({ key: "", value: "" });
      }}
    >
      + Add Metric
    </button>
  </div>

  {growth.length > 0 && (
    <div className="fundamentals-list">
      {growth.map((item, idx) => (
        <div key={idx} className="fundamental-item">
          <span><strong>{item.key}:</strong> {item.value}</span>
          <button onClick={() => setGrowth(growth.filter((_, i) => i !== idx))}>
            Remove
          </button>
        </div>
      ))}
    </div>
  )}
</div>

// Add this in the cancel button handler (around line 430):
onClick={() => {
  setEditIndex(null);
  setNewDetail({
    company_id: "",
    reports: [],
    key_people: [],
    about_company: "",
    exchange_type: "",
    highlights: [],
  });
  setFundamentals([]);
  setGrowth([]);  // 🆕 Reset growth
}} */}



        <div className="form-section">
          <h3 style={{ color: "white" }}>Highlights</h3>
          <div style={{ display: "flex", gap: "10px" }}>
            <textarea
              placeholder="Enter a highlight (use **bold** for bold text)"
              value={highlightText}
              onChange={(e) => setHighlightText(e.target.value)}
              onPaste={handleHighlightPaste}
              style={{ flex: 1, marginBottom: "10px" }}
            />
            <button
              type="button"
              className="add-person-btn"
              onClick={handleAddHighlight}
            >
              {editingHighlightIndex !== null ? "Update Highlight" : "+ Add Highlight"}
            </button>
          </div>
          {newDetail.highlights.length > 0 && (
            <div className="highlights-list">
              {newDetail.highlights.map((highlight, idx) => (
                <div key={idx} className="highlight-item">
                  <div className="highlight-controls">
                    <button
                      type="button"
                      onClick={() => handleMoveHighlight(idx, "up")}
                      disabled={idx === 0}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveHighlight(idx, "down")}
                      disabled={idx === newDetail.highlights.length - 1}
                      title="Move down"
                    >
                      ↓
                    </button>
                  </div>
                  <div
                    className="highlight-content"
                    dangerouslySetInnerHTML={{ __html: highlight }}
                  />
                  <div className="highlight-actions">
                    <button type="button" onClick={() => handleEditHighlight(idx)}>
                      Edit
                    </button>
                    <button type="button" onClick={() => handleRemoveHighlight(idx)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Key People Section */}
        <div className="key-people-section">
          <h3>Key People</h3>
          <div className="key-person-form">
            <input
              type="text"
              placeholder="Title (e.g., CHAIRMAN & CEO)"
              value={keyPersonForm.title}
              onChange={(e) => setKeyPersonForm({ ...keyPersonForm, title: e.target.value })}
            />
            <input
              type="text"
              placeholder="Name"
              value={keyPersonForm.name}
              onChange={(e) => setKeyPersonForm({ ...keyPersonForm, name: e.target.value })}
            />
            <button type="button" className="add-person-btn" onClick={handleAddKeyPerson}>
              + Add Person
            </button>
          </div>

          {newDetail.key_people.length > 0 && (
            <div className="key-people-list">
              {newDetail.key_people.map((person, index) => (
                <div key={index} className="key-person-item">
                  <span>
                    <strong>{person.title}:</strong> {person.name}
                  </span>
                  <button onClick={() => handleRemoveKeyPerson(index)}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button className="save-btn" onClick={handleSaveDetails}>
          {editIndex !== null ? "Update Details" : "Add Details"}
        </button>
        {editIndex !== null && (
          <button
            className="cancel-btn"
            onClick={() => {
              setEditIndex(null);
              setNewDetail({
                company_id: "",
                reports: [],
                key_people: [],
                about_company: "",
                exchange_type: "",
                highlights: [],
              });
              setFundamentals([]);
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {/* List Section */}
      <div className="details-list">
        <h2>Company Details List ({companyDetails.length})</h2>

        {loading ? (
          <p className="empty-text">Loading...</p>
        ) : error ? (
          <p className="empty-text" style={{ color: "#ef4444" }}>{error}</p>
        ) : companyDetails.length === 0 ? (
          <p className="empty-text">No company details added yet.</p>
        ) : (
          companyDetails.map((detail, index) => (
            <div key={detail.id} className="detail-card">
              <h3>{getCompanyName(detail.company_id)}</h3>

              {/* Exchange Type Display
              {detail.exchange_type && (
                <p>
                  <strong>Exchange:</strong> {detail.exchange_type}
                </p>
              )} */}

              {/* Reports Display */}
              {/* {detail.reports && detail.reports.length > 0 && (
                <div className="reports-display">
                  <h4>📊 Reports</h4>
                  <div className="reports-grid">
                    {detail.reports.map((report, idx) => (
                      <div key={idx} className="report-card">
                        <span className="report-type-badge">{report.report_type}</span>
                        <span className="report-year-badge">{report.report_year}</span>
                        <a
                          href={report.report_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="report-link"
                        >
                          📄 View Report →
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )} */}

              {/* About Company Display */}
              {/* {detail.about_company && (
                <div className="about-company-section">
                  <strong>About Company:</strong>
                  <div
                    className="about-content"
                    dangerouslySetInnerHTML={{ __html: detail.about_company }}
                  />
                </div>
              )} */}

              {/* Fundamentals Display */}
              {detail.fundamentals && Object.keys(detail.fundamentals).length > 0 && (
                <div className="fundamentals-display">
                  <h4 className="fundamentals-section">Fundamentals</h4>
                  <div className="fundamentals-grid">
                    {Object.entries(detail.fundamentals).map(([k, v]) => (
                      <div key={k} className="fundamental-box">
                        <span className="fund-key">{k}</span>
                        <span className="fund-value">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Growth Display
{detail.growth && Object.keys(detail.growth).length > 0 && (
  <div className="fundamentals-display">
    <h4 className="fundamentals-section">📈 Growth Metrics</h4>
    <div className="fundamentals-grid">
      {Object.entries(detail.growth).map(([k, v]) => (
        <div key={k} className="fundamental-box">
          <span className="fund-key">{k}</span>
          <span className="fund-value">{v}</span>
        </div>
      ))}
    </div>
  </div>
)} */}

              {/* Highlights Display */}
              {/* {detail.highlights && detail.highlights.length > 0 && (
                <div className="highlights-display">
                  <h4>Highlights</h4>
                  <ul>
                    {detail.highlights.map((highlight, idx) => (
                      <li
                        key={idx}
                        dangerouslySetInnerHTML={{ __html: highlight }}
                      />
                    ))}
                  </ul>
                </div>
              )} */}

              {/* Key People Display
              {detail.key_people && detail.key_people.length > 0 && (
                <div className="key-people-display">
                  <strong>Key People:</strong>
                  <ul>
                    {detail.key_people.map((person, idx) => (
                      <li key={idx}>
                        {person.title}: {person.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )} */}

              <div className="action-buttons">
                <button onClick={() => handleEditDetails(index)}>Edit</button>
                <button className="delete-btn" onClick={() => handleDeleteDetails(detail.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>


      <Footer />

    </div>
  );
};

export default CompanyDetails;